import { inject, readonly, ref } from 'vue'
import { ApiError } from '@drama/api-sdk'

export const adminAuthKey = Symbol('visualGameAdmin.auth')

/**
 * @param {ReturnType<import('../api/client.js').createVisualGameAdminClient>} api
 */
export function createAdminAuth(api) {
  const account = ref(null)
  const loading = ref(false)
  const forbidden = ref(false)
  const error = ref('')

  async function ensureSession() {
    forbidden.value = false
    let token = api.tokenStore.getAccessToken()
    if (!token) {
      if (!api.tokenStore.getRefreshToken()) {
        account.value = null
        return false
      }
      try {
        token = await api.tokenStore.refresh()
      } catch {
        api.tokenStore.clear()
        account.value = null
        return false
      }
    }
    loading.value = true
    error.value = ''
    try {
      const profile = await api.account.getProfile()
      account.value = profile
      forbidden.value = !isOperatorRole(profile.role)
      return true
    } catch (err) {
      error.value = err.message
      // getProfile() 已经在 401 时经 transport 的 withRefresh 静默刷新过一次；这里还是
      // ApiError(401) 说明 refresh token 也失效了，才是真的要重新登录。网络错误/超时/5xx
      // 不是"没登录"——路由每次跳转都会跑一遍这个检查，一次网络抖动就把仍然有效的 token
      // 清掉、逼用户重新输密码，这是之前"总是要求重登"的根因。
      if (err instanceof ApiError && err.status === 401) {
        api.tokenStore.clear()
        account.value = null
        return false
      }
      // token 还在、上次登录的 account 也还在，不因为这次探活失败就把人踢回登录页。
      return account.value !== null
    } finally {
      loading.value = false
    }
  }

  /**
   * @param {{ email: string, password: string }} input
   */
  async function login(input) {
    loading.value = true
    error.value = ''
    forbidden.value = false
    try {
      const session = await api.auth.login(input)
      api.tokenStore.setTokens(session)
      account.value = session.account
      forbidden.value = !isOperatorRole(session.account.role)
      return session.account
    } catch (err) {
      error.value = err.message
      throw err
    } finally {
      loading.value = false
    }
  }

  async function logout() {
    const refreshToken = api.tokenStore.getRefreshToken()
    if (refreshToken) {
      await api.auth.logout({ refresh_token: refreshToken })
    }
    api.tokenStore.clear()
    account.value = null
    forbidden.value = false
  }

  return {
    account: readonly(account),
    loading: readonly(loading),
    forbidden: readonly(forbidden),
    error: readonly(error),
    ensureSession,
    login,
    logout,
  }
}

export function useAdminAuth() {
  const auth = inject(adminAuthKey)
  if (!auth) throw new Error('useAdminAuth: provider is missing')
  return auth
}

/** @param {string} role */
export function isOperatorRole(role) {
  return role === 'admin' || role === 'operator'
}
